import { useEffect, useState, type ReactNode } from "react";
import { resolveTenant, brandingCssVars, type TenantResolved } from "../lib/tenant";

/**
 * TenantThemeProvider — applies per-tenant CSS variable overrides to
 * the app root (Phase 13 Loop 1). Wraps `<App />` in main.tsx.
 *
 * Renders children immediately; CSS vars apply once `/v1/tenant/resolve`
 * returns. No flash-of-wrong-brand because the default NEXIRT palette
 * is already in the tokens.css cascade — tenant overrides replace
 * specific vars without repainting the full theme.
 */

export default function TenantThemeProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<TenantResolved | null>(null);

  useEffect(() => {
    let alive = true;
    resolveTenant().then((t) => {
      if (!alive) return;
      setTenant(t);
      const vars = brandingCssVars(t.branding);
      const root = document.documentElement;
      for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
      root.setAttribute("data-tenant", t.tenantId);
    });
    return () => { alive = false; };
  }, []);

  return (
    <>
      {/* Expose tenant info to descendants via a shared context if/when
          consumers need it. For now, the CSS variables + data-tenant
          attribute are enough to drive branding + conditional rendering
          ("Powered by NEXIRT" footer, tenant-scoped JSON-LD). */}
      {tenant ? <TenantInfo tenant={tenant} /> : null}
      {children}
    </>
  );
}

/**
 * Hidden component that injects tenant metadata into a global so
 * non-React consumers (e.g. the footer's "Powered by NEXIRT" toggle,
 * JSON-LD ld+json generator) can read it without prop drilling.
 */
declare global {
  interface Window {
    __nexirt_tenant?: TenantResolved;
  }
}

function TenantInfo({ tenant }: { tenant: TenantResolved }) {
  useEffect(() => {
    window.__nexirt_tenant = tenant;
  }, [tenant]);
  return null;
}
